import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon.tsx";

/** What the picture and video uploads take (the API checks the content again). */
export const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/webp"];
export const VIDEO_ACCEPT = ["video/mp4", "video/webm", "video/quicktime", ".mp4", ".webm", ".mov"];

/** True when the file is one of `accept`: MIME types ("image/png"), families ("video/*") or extensions (".mov"). */
export function accepts(file: File, accept: string[]): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return accept.some((a) => {
    const rule = a.trim().toLowerCase();
    if (rule.startsWith(".")) return name.endsWith(rule);
    if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/**
 * Wraps an upload button: files dragged from the computer can be dropped
 * anywhere on it. Files of another kind are refused with their names, the rest
 * go to `onFiles` as if picked with the button (which stays, for keyboards and phones).
 */
export function DropZone({
  accept,
  multiple = false,
  disabled = false,
  hint,
  onFiles,
  onRefused,
  children,
}: {
  accept: string[];
  multiple?: boolean;
  disabled?: boolean;
  /** "Drop pictures here" */
  hint: string;
  onFiles: (files: File[]) => void;
  onRefused?: (names: string[]) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  // dragenter/dragleave fire for every child: count them to know when the pointer really left.
  const depth = useRef(0);
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

  return (
    <div
      className={`drop-zone${over ? " is-over" : ""}${disabled ? " is-disabled" : ""}`}
      onDragEnter={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const all = Array.from(e.dataTransfer.files);
        const ok = all.filter((f) => accepts(f, accept));
        const refused = all.filter((f) => !ok.includes(f)).map((f) => f.name);
        if (refused.length) onRefused?.(refused);
        if (ok.length) onFiles(multiple ? ok : ok.slice(0, 1));
      }}
    >
      {children}
      <span className="drop-hint small" aria-hidden="true">
        <Icon name="upload" size={14} />
        {over ? t("drop.release") : hint}
      </span>
    </div>
  );
}
